import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  Optional,
  Output,
} from "@angular/core";
import { NgControl } from "@angular/forms";

export const NUMERIC_INPUT_LOCALE = new InjectionToken<string | string[]>(
  "NUMERIC_INPUT_LOCALE"
);

@Directive({
  selector: "[dlNumericInput]",
})
export class NumericInputDirective implements AfterViewInit, OnDestroy {
  @Input() min?: number;
  @Input() max?: number;
  @Output() localized = new EventEmitter<string>();

  private readonly locales: string | string[];
  private readonly decimalSeparator: string;
  private readonly thousandsSeparator: string;
  private formElement: HTMLFormElement | null = null;

  constructor(
    private readonly hostElement: ElementRef<HTMLInputElement>,
    @Optional() @Inject(NUMERIC_INPUT_LOCALE) locales?: string | string[],
    @Optional() private readonly ngControl?: NgControl
  ) {
    this.locales = locales || "en";
    this.decimalSeparator =
      (1.1).toLocaleString(this.locales, { useGrouping: false }).replace(/[0-9]/g, "") || ".";
    this.thousandsSeparator =
      (1234.5)
        .toLocaleString(this.locales)
        .replace(/[0-9]/g, "")
        .replace(this.decimalSeparator, "") || ",";
  }

  ngAfterViewInit(): void {
    this.el.setAttribute("type", "text");
    this.el.setAttribute("inputmode", "decimal");
    this.el.removeAttribute("pattern");
    this.formElement = this.el.form;
    this.formElement?.addEventListener("submit", this.onFormSubmit);
  }

  ngOnDestroy(): void {
    this.formElement?.removeEventListener("submit", this.onFormSubmit);
  }

  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    this.el.setCustomValidity("");

    const key = event.key || String.fromCharCode(event.keyCode || 0);
    const currentValue = this.el.value;
    const cursorPosition = this.el.selectionStart || 0;
    const isNumber = /^[0-9]$/.test(key);
    const isActionKey = event.ctrlKey || event.metaKey;
    const isShortcut = isActionKey && ["a", "c", "v", "x"].includes(key.toLowerCase());
    const isNavigationKey = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Escape",
      "Tab",
      "Enter",
    ].includes(key);
    const hasDecimalSeparator = currentValue.includes(this.decimalSeparator);
    const separatorFollowsSign = currentValue.includes("-") && cursorPosition <= 1;
    const canAddDecimalSeparator =
      key === this.decimalSeparator && !hasDecimalSeparator && !separatorFollowsSign;
    const canAddSign =
      key === "-" && !currentValue.includes("-") && cursorPosition === 0 &&
      !currentValue.startsWith(this.decimalSeparator);

    if (
      !isNumber &&
      !isShortcut &&
      !isNavigationKey &&
      !canAddDecimalSeparator &&
      !canAddSign
    ) {
      event.preventDefault();
    }
  }

  @HostListener("change")
  onChange(): void {
    this.setValue(this.el.value);
  }

  @HostListener("paste", ["$event"])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.setValue(event.clipboardData?.getData("text/plain") || "");
  }

  @HostListener("drop", ["$event"])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.setValue(event.dataTransfer?.getData("text") || "");
  }

  private setValue(value: string): void {
    const normalized = value
      .split(this.thousandsSeparator)
      .join("")
      .replace(this.decimalSeparator, ".");
    const parsed = Number(normalized);
    const formattedValue = Number.isFinite(parsed) ? parsed : 0;

    this.localized.emit(formattedValue.toLocaleString(this.locales));
    this.el.value = String(formattedValue);
    this.ngControl?.control?.patchValue(formattedValue);
  }

  private readonly onFormSubmit = (event: Event): void => {
    const value = Number(this.el.value);
    const errorMessage =
      this.min !== undefined && value < this.min
        ? `Value must be greater than or equal to ${this.min}.`
        : this.max !== undefined && value > this.max
          ? `Value must be less than or equal to ${this.max}.`
          : "";

    this.el.setCustomValidity(errorMessage);
    if (errorMessage) {
      event.preventDefault();
      this.el.reportValidity();
    }
  };

  private get el(): HTMLInputElement {
    return this.hostElement.nativeElement;
  }
}